import { type FlashList } from '@shopify/flash-list';
import { type ReactNode, useMemo, useRef, useState } from 'react';

import { ScrollRestorationKey } from '@fhs/utils';

import { anchorSectionListContext } from './context';
import type { FlashListDatum, SectionType, TileSizeType } from './types';

type AnchorSectionListProps = {
  sections: Array<SectionType>;
  defaultSectionId?: SectionType['id'];
  size?: TileSizeType;
  children: ReactNode;
  scrollRestorationKey?: ScrollRestorationKey;
};
export function AnchorSectionList(props: AnchorSectionListProps) {
  const flashListRef = useRef<FlashList<FlashListDatum>>(null);

  const [activeSectionId, setActiveSectionId] = useState('');
  const [manualActiveSectionId, setManualActiveSectionId] = useState('');

  const flashListData = useMemo(() => {
    const data: Array<FlashListDatum> = [];

    for (const section of props.sections) {
      data.push({ type: 'section', section } as const);

      if (Array.isArray(section.items)) {
        data.push(
          ...section.items.map(
            (item, indexWithinSection) =>
              ({
                type: 'item',
                parentSectionId: section.id,
                indexWithinSection,
                item,
              } as const)
          )
        );
      } else {
        data.push({ type: 'custom', section } as const);
      }
    }

    return data;
  }, [props.sections]);

  const contextValue = useMemo(() => {
    return {
      sections: props.sections,
      defaultSectionId: props.defaultSectionId,
      size: props.size ?? 'lg',
      flashListRef,
      activeSectionId,
      setActiveSectionId,
      manualActiveSectionId,
      setManualActiveSectionId,
      flashListData,
      scrollRestorationKey: props.scrollRestorationKey,
    };
  }, [
    props.sections,
    props.defaultSectionId,
    props.size,
    props.scrollRestorationKey,
    activeSectionId,
    manualActiveSectionId,
    flashListData,
  ]);

  return (
    <anchorSectionListContext.Provider value={contextValue}>
      {props.children}
    </anchorSectionListContext.Provider>
  );
}
